/**
 * ClaimTag.jsx — v2 widget for anonymous @-tag claim & validation.
 *
 * Ports the v1 dual-identity flow (src/web/public/js/main.js renderDualIdentity
 * + the legacy claim modal) onto the v2 desktop. The widget POSTs to
 * `/api/claim-tag` registers an unverified tag for the later Discord-linking
 * flow. Personal sales require verified Discord authentication.
 *
 * CSP: handlers attach via Solid's onClick / onSubmit (compiled to
 * addEventListener), no inline `onclick=""` markup. No HTML strings.
 */

import { createSignal, Show, createMemo } from 'solid-js';
import wm from '../lib/wmStore.js';
import toast from '../lib/toastStore.js';

const LS_TAG   = 'redfur-claimed-tag';
function readSavedTag() {
  try { return localStorage.getItem(LS_TAG) || ''; } catch { return ''; }
}

/** Normalise user input to `@Foo` form (matches server normalizeAtTag). */
function normalizeInput(raw) {
  if (!raw) return '';
  let v = String(raw).trim();
  if (!v) return '';
  if (v.startsWith('#')) v = '@' + v.slice(1);
  else if (!v.startsWith('@')) v = '@' + v;
  return v;
}

export default function ClaimTag(props) {
  const initial = props.settings?.tag || readSavedTag();
  const [tag, setTag]         = createSignal(initial);
  const [busy, setBusy]       = createSignal(false);
  const [status, setStatus]   = createSignal(initial ? 'saved' : '');
  const [verified, setVerified] = createSignal(false);
  const [pending, setPending]   = createSignal(false);
  const [linked, setLinked]     = createSignal(false);
  // Sprint 16 #308: conflict-resolution state.
  // When /api/claim-tag returns 409 with `conflict: true`, we surface
  // a "Request transfer" CTA that POSTs /api/v2/claims/switch.
  const [conflict, setConflict]       = createSignal(null);
  const [switchBusy, setSwitchBusy]   = createSignal(false);
  const [switchSent, setSwitchSent]   = createSignal('');

  const loggedIn = createMemo(() => !!props.identity);

  function persistTagSetting(val) {
    if (!props.widget?.id) return;
    try {
      wm.setSettings(props.widget.id, { ...(props.widget.settings || {}), tag: val || null });
    } catch { /* ignore */ }
  }

  function notifyClaimedTagChange(val) {
    if (typeof window === 'undefined') return;
    try {
      window.dispatchEvent(new CustomEvent('redfur:claimed-tag-change', { detail: { tag: val || '' } }));
    } catch { /* ignore */ }
  }

  async function submitClaim(ev) {
    if (ev) ev.preventDefault();
    if (busy()) return;
    const normal = normalizeInput(tag());
    if (!normal || normal.length < 2) {
      toast.error('Please enter a valid @-tag.');
      return;
    }
    setBusy(true);
    setConflict(null);
    setSwitchSent('');
    try {
      const res = await fetch('/api/claim-tag', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ esoTag: normal }),
      });
      let body = null;
      try { body = await res.json(); } catch { /* ignore */ }
      if (!res.ok) {
        setLinked(!!(body && body.linked));
        if (body && body.conflict && body.claimedBy) {
          setConflict({
            tag: normal,
            displayName: body.claimedBy.displayName || normal,
            canRequestSwitch: !!body.canRequestSwitch,
          });
        }
        throw new Error((body && body.error) || `HTTP ${res.status}`);
      }
      try {
        localStorage.setItem(LS_TAG, body.esoTag);
      } catch { /* ignore quota errors */ }
      setTag(body.esoTag);
      persistTagSetting(body.esoTag);
      notifyClaimedTagChange(body.esoTag);
      setVerified(!!body.inDatabase);
      setPending(!body.inDatabase);
      setStatus('saved');
      if (body.alreadyClaimed) {
        toast.success(body.message || 'Tag already linked to your account.');
      }
    } catch (err) {
      const msg = err && err.message ? err.message : 'Failed to claim tag.';
      const detail = conflict()
        ? undefined  // dedicated conflict UI renders below; skip toast detail
        : linked()
          ? 'That tag is already linked to a Discord account — log in with Discord instead, or contact staff if you believe this is an error.'
          : undefined;
      toast.error(msg, { detail });
      setStatus('');
    } finally {
      setBusy(false);
    }
  }

  async function requestSwitch() {
    const c = conflict();
    if (!c || switchBusy()) return;
    setSwitchBusy(true);
    try {
      const res = await fetch('/api/v2/claims/switch', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ esoTag: c.tag }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      const msg = body.duplicate
        ? 'Already queued — Jone and Jode will arbitrate.'
        : 'Request submitted. An admin will review.';
      setSwitchSent(msg);
      toast.success(msg);
    } catch (err) {
      toast.error('Could not submit transfer request', { detail: err?.message });
    } finally {
      setSwitchBusy(false);
    }
  }

  function clearSaved() {
    try { localStorage.removeItem(LS_TAG); } catch { /* ignore */ }
    setTag('');
    persistTagSetting('');
    notifyClaimedTagChange('');
    setStatus('');
    setVerified(false);
    setPending(false);
    setLinked(false);
    setConflict(null);
    setSwitchSent('');
  }

  return (
    <div class="v2-widget v2-claim-tag">
      <div class="v2-widget__scroll" style={{ padding: 'var(--sp-2)' }}>
        <Show when={loggedIn()} fallback={null}>
          <div style={{ color: 'var(--t-green-dim)', 'font-size': 'var(--fs-sm)', 'margin-bottom': 'var(--sp-2)' }}>
            You are logged in with Discord. Manage your @-tag from <strong>Settings ⚙</strong>;
            this widget is intended for anonymous device-only claims.
          </div>
        </Show>

        <div style={{ 'font-family': 'var(--font-terminal)', 'font-size': 'var(--fs-lg)', color: 'var(--fissal)', 'margin-bottom': 'var(--sp-2)' }}>
          🏷️ Claim your ESO @-tag
        </div>
        <p style={{ color: 'var(--t-green-dim)', 'font-size': 'var(--fs-sm)', 'margin-top': 0 }}>
          Save your ESO @-tag here, then sign in with Discord to verify ownership
          and view personal sales.
        </p>

        <form onSubmit={submitClaim} style={{ display: 'flex', gap: 'var(--sp-2)', 'margin-top': 'var(--sp-2)' }}>
          <input
            type="text"
            class="form-input"
            placeholder="@YourESOTag"
            value={tag()}
            maxLength={64}
            autocomplete="off"
            spellcheck={false}
            onInput={(e) => setTag(e.currentTarget.value)}
            style={{ flex: 1, 'font-family': 'var(--font-mono)' }}
          />
          <button
            type="submit"
            class="btn btn-primary"
            disabled={busy()}
          >
            {busy() ? '…' : 'Claim'}
          </button>
        </form>

        <Show when={status() === 'saved'}>
          <div style={{ 'margin-top': 'var(--sp-2)', 'font-size': 'var(--fs-xs)', color: 'var(--gold, var(--fissal))' }}>
            <Show when={verified()}>✅ Tag verified: <strong>{tag()}</strong></Show>
            <Show when={pending() && !verified()}>
              ⏳ Tag set: <strong>{tag()}</strong> — not in the sales DB yet.
              It will appear after the next sync.
            </Show>
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-2)', 'margin-top': 'var(--sp-2)' }}>
            <button type="button" class="btn" onClick={clearSaved} disabled={busy()}>Clear</button>
          </div>
        </Show>

        <Show when={conflict()}>
          <div style={{
            'margin-top': 'var(--sp-3)',
            padding: 'var(--sp-2) var(--sp-3)',
            border: '1px solid var(--bad, #ff6b6b)',
            'border-radius': 'var(--r-md, 6px)',
            background: 'rgba(255, 107, 107, 0.06)',
            color: 'var(--t-white)',
            'font-size': 'var(--fs-sm)',
          }}>
            <div style={{ 'font-family': 'var(--font-terminal)', color: 'var(--bad, #ff6b6b)', 'margin-bottom': 'var(--sp-1)' }}>
              ⚠ Frequency already held
            </div>
            <p style={{ margin: 0, color: 'var(--t-green-dim)' }}>
              <strong style={{ color: 'var(--t-white)' }}>{conflict().displayName}</strong>{' '}
              already holds <code style={{ color: 'var(--fissal)' }}>{conflict().tag}</code>.
              If it's yours, request a transfer — Jone and Jode will arbitrate.
            </p>
            <Show when={!switchSent()}>
              <Show when={conflict().canRequestSwitch} fallback={
                <p style={{ 'margin-top': 'var(--sp-2)', 'margin-bottom': 0, color: 'var(--t-green-dim)', 'font-size': 'var(--fs-xs)' }}>
                  <a href="/auth/login?returnTo=/v2/" style={{ color: 'var(--fissal)' }}>Log in with Discord</a> to request a transfer.
                </p>
              }>
                <button
                  type="button"
                  class="btn"
                  onClick={requestSwitch}
                  disabled={switchBusy()}
                  style={{ 'margin-top': 'var(--sp-2)' }}
                >
                  {switchBusy() ? 'Submitting…' : 'Request transfer'}
                </button>
              </Show>
            </Show>
            <Show when={switchSent()}>
              <p style={{ 'margin-top': 'var(--sp-2)', 'margin-bottom': 0, color: 'var(--fissal)', 'font-size': 'var(--fs-xs)' }}>
                ✅ {switchSent()}
              </p>
            </Show>
          </div>
        </Show>

        <Show when={!loggedIn()}>
          <div style={{ 'margin-top': 'var(--sp-3)', 'padding-top': 'var(--sp-2)', 'border-top': '1px dashed var(--t-green-dim)', color: 'var(--t-green-dim)', 'font-size': 'var(--fs-xs)' }}>
            Prefer to log in with Discord? <a href="/auth/login?returnTo=/v2/" style={{ color: 'var(--fissal)' }}>Sign in</a> — Discord login also unlocks Fissal AI and personal sync points.
          </div>
        </Show>
      </div>
    </div>
  );
}

/** Helper used by WelcomeLogin to spawn the claim widget on demand. */
export function launchClaimTag() {
  // Prefer transient (session-only) so it doesn't clutter the saved layout.
  if (wm && typeof wm.addTransientWidget === 'function') {
    return wm.addTransientWidget('claim-tag', {});
  }
  if (wm && typeof wm.addWidget === 'function') {
    return wm.addWidget('claim-tag');
  }
  return null;
}
